import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import Pagination from '@/components/CommonComponents/Pagination';
import ServerSearchFilter from '@/components/CommonComponents/ServerSearchFilter';
import StatCard from '@/components/CommonComponents/StatCard';
import { showToast } from '@/helper/toast.helper';
import { usePermission } from '@/hooks/usePermission';
import { AppDispatch, RootState } from '@/store';
import {
  clearProjectUsers,
  deleteProjectUser,
  fetchProjectUsers,
  toggleProjectUserStatus,
} from '@/store/slices/projectUsersSlice/projectUsersSlice';
import { RowPermissions } from '@/types/surveySet.types';
import { CircleUser, Eye, EyeOff } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AddUserToProjectModal from '../CommonComponents/AddUserToProjectModal';
import DeleteModal from '../CommonComponents/DeleteModal';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

type User = {
  id: string;
  userId: number;
  name: string;
  mobileNumber: string;
  siteLocation: string;
  status: string;
  connectedOn: string;
  _permissions?: RowPermissions;
};

const UserManagementProject: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Select All');
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userNameToDelete, setUserNameToDelete] = useState<string>('');
  const [userIdToDelete, setUserIdToDelete] = useState<number | null>(null);

  const selectedProject = useSelector((state: RootState) => state.projects.selected) as any;
  const {
    data: projectUsersData,
    loading: loadingUsers,
    error: usersError,
  } = useSelector((state: RootState) => state.projectUsers);

  const resolvedProjectId = useMemo(() => {
    const locState: any = location.state;
    return locState?.projectId || locState?.project?.id || selectedProject?.id;
  }, [location.state, selectedProject?.id]);

  const users = useMemo(() => {
    return projectUsersData.map((apiUser: any) => {
      const rawDate = apiUser.createdAt || '';
      const formattedDate = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';

      return {
        id: apiUser.blueNumberId || '',
        userId: apiUser.userId,
        name: apiUser.name || '',
        mobileNumber: `${apiUser.countryCode || ''} ${apiUser.phoneNumber || ''}`,
        siteLocation: apiUser.surveySet?.site?.name || '',
        status: apiUser.status ? 'Active' : 'Inactive',
        connectedOn: formattedDate,
      };
    });
  }, [projectUsersData]);

  useEffect(() => {
    return () => {
      dispatch(clearProjectUsers());
    };
  }, [dispatch]);

  useEffect(() => {
    if (resolvedProjectId) {
      dispatch(fetchProjectUsers(Number(resolvedProjectId)));
    }
  }, [resolvedProjectId, dispatch]);

  const stats = useMemo(() => {
    const total = projectUsersData.length;
    const active = projectUsersData.filter((u) => u.status).length;
    const inactive = total - active;

    return {
      totalUsers: total,
      active,
      inactive,
    };
  }, [projectUsersData]);

  const permissions = {
    create: usePermission('write', 'project_users'),
    edit: usePermission('edit', 'project_users'),
    export: usePermission('export', 'project_users'),
    delete: usePermission('delete', 'project_users'),
  };

  const openDeleteModal = (blueNumberId: string) => {
    const selected = users.find((u) => u.id === blueNumberId);
    if (!selected) return;

    setUserIdToDelete(selected.userId);
    setUserNameToDelete(selected.name !== 'N/A' ? selected.name : selected?.mobileNumber);
    setIsDeleteModalOpen(true);
  };

  const toggleStatus = (blueNumberId: string) => {
    const apiUser = projectUsersData.find((u) => u.blueNumberId === blueNumberId);
    if (!apiUser) return;

    dispatch(
      toggleProjectUserStatus({
        projectId: resolvedProjectId,
        userId: apiUser.userId,
        status: !apiUser.status,
      }),
    );
  };

  const filteredUsers = useMemo(() => {
    const query = search.toLowerCase();

    return users.filter((u: User) => {
      const id = String(u.id || '').toLowerCase();
      const mobile = String(u.mobileNumber || '').toLowerCase();
      const site = String(u.siteLocation || '').toLowerCase();
      const name = String(u.name || '').toLowerCase();

      const matches =
        id.includes(query) ||
        mobile.includes(query) ||
        site.includes(query) ||
        name.includes(query);

      const userStatus = (u.status || '').trim().toLowerCase();
      const filterStatus = (statusFilter || '').trim().toLowerCase();

      if (filterStatus === 'select all') return matches;

      return matches && userStatus === filterStatus;
    });
  }, [users, search, statusFilter]);

  const totalRecords = filteredUsers.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = filteredUsers.slice(startIndex, startIndex + pageSize);

  const usersWithPermissions = useMemo(() => {
    return currentData.map((user) => ({
      ...user,
      _permissions: permissions,
    }));
  }, [currentData, permissions]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);

    if (resolvedProjectId) {
      dispatch(fetchProjectUsers(Number(resolvedProjectId)));
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAddUser = () => setShowAddUserModal(true);

  const handleExport = async () => {
    try {
      const state: any = location.state;
      const projectId = state?.projectId || state?.project?.id || selectedProject?.id;

      if (!projectId) {
        alert('Project ID not found!');
        return;
      }

      const csv = [
        ['User Name', 'Name', 'Mobile Number', 'Site Location', 'Status', 'Connected On'],
        ...filteredUsers.map((u) => [
          u.id,
          u.name,
          u.mobileNumber,
          u.siteLocation,
          u.status,
          u.connectedOn,
        ]),
      ]
        .map((row) => row.map((cell) => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project-users-${projectId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export users');
    }
  };

  const handleDeleteUser = async () => {
    if (!resolvedProjectId || userIdToDelete === null) return;

    try {
      await dispatch(
        deleteProjectUser({
          projectId: Number(resolvedProjectId),
          userId: userIdToDelete,
        }),
      ).unwrap();

      showToast.success('User deleted successfully');
    } catch (error: any) {
    } finally {
      setIsDeleteModalOpen(false);
      setUserIdToDelete(null);
      setUserNameToDelete('');
    }
  };

  const usersData = [
    {
      id: 'total_users',
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString('en-IN') || 0,
      icon: CircleUser,
      iconBg: 'bg-[#4C86E7]/50',
    },
    {
      id: 'active_user',
      title: 'Active Users',
      value: stats.active || 0,
      icon: Eye,
      iconBg: 'bg-[#0BA96F80]',
    },
    {
      id: 'inactive-user',
      title: 'Inactive Users',
      value: stats.inactive || 0,
      icon: EyeOff,
      iconBg: 'bg-[#E24A4A]/50',
    },
  ];

  const projectUserColumns: Column<User>[] = [
    { key: 'mobileNumber', header: 'Mobile Number' },
    {
      key: 'siteLocation',
      header: 'Site Location',
      render: (value) => (
        <span className="bg-gray-200 text-gray-800 px-4 py-1 rounded-full text-sm">
          {value ? value : 'N/A'}{' '}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <span
          className={`px-3 py-1 rounded-full text-sm font-semibold ${
            value === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {value}
        </span>
      ),
    },
    { key: 'connectedOn', header: 'Connected On' },
  ];

  return (
    <PageHeaderWrapper
      title="Project Users"
      description="Manage all users of the project."
      onAdd={handleAddUser}
      onExport={handleExport}
      extraLabel="Return to project dashboard"
      onExtra={() => window.history.back()}
      showExtra={true}
      addLabel="Add Users"
      exportLabel="Export"
      permissions={permissions}
    >
      <ServerSearchFilter
        fields={[
          { type: 'search', key: 'search', placeholder: 'Search users...' },
          { type: 'radio-select', key: 'status', label: 'Status', options: ['Active', 'Inactive'] },
        ]}
        onChange={(filters) => {
          setSearch(filters.search || '');
          setStatusFilter(filters.status || 'Select All');
        }}
        noBg
      />

      {/* Stats */}
      <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        {usersData.map((stat) => (
          <StatCard
            key={stat.id}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            iconBg={stat.iconBg}
          />
        ))}
      </div>

      {/* User Table */}
      <DataTable
        listTitle="User List"
        columns={projectUserColumns}
        data={usersWithPermissions}
        loading={loadingUsers}
        onView={(row) => {
          setSelectedUser(row);
          setIsUserDetailsOpen(true);
        }}
        onDelete={(row) => openDeleteModal(row.id)}
        onStatusToggle={(id) => toggleStatus(id)}
      />

      {/* Pagination */}
      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
      />

      <ViewDetailsModal
        isOpen={isUserDetailsOpen}
        onClose={() => setIsUserDetailsOpen(false)}
        title="User Details"
        data={selectedUser || {}}
        fields={[
          {
            label: 'Mobile Number',
            value: selectedUser?.mobileNumber || 'N/A',
          },
          {
            label: 'Site Location',
            value: selectedUser?.siteLocation || 'N/A',
          },
          {
            label: 'Connected On',
            value: selectedUser?.connectedOn || 'N/A',
          },
          {
            label: 'Status',
            value: selectedUser?.status || 'N/A',
          },
        ]}
      />

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserToProjectModal
          isOpen={showAddUserModal}
          onClose={handleCloseAddUserModal}
          projectId={Number(resolvedProjectId)}
        />
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserIdToDelete(null);
          setUserNameToDelete('');
        }}
        onDelete={handleDeleteUser}
        itemName={userNameToDelete}
        pageTitle="User"
      />
    </PageHeaderWrapper>
  );
};

export default UserManagementProject;
